import { useState, useRef, useEffect } from 'react';
import { Folder } from 'lucide-react';
import { KnowledgeItem } from '../services/api';

interface MoveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (targetFolderId: string | null) => void;
  items: KnowledgeItem[];
  currentItem: KnowledgeItem;
}

export function MoveDialog({ isOpen, onClose, onMove, items, currentItem }: MoveDialogProps) {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.classList.add('modal-open');
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, onClose]);

  const renderFolderOption = (item: KnowledgeItem, level = 0) => {
    // Показываем только папки - файлы нельзя выбрать как цель перемещения
    // Исключаем текущий элемент (если это папка) и его родительскую папку
    if (item.itemType !== 'folder' || item.id === currentItem.id) {
      return null;
    }

    return (
      <div key={item.id}>
        <button
          onClick={() => setSelectedFolder(item.id)}
          className={`w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center ${
            selectedFolder === item.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
          }`}
          style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
        >
          <Folder className="w-4 h-4 mr-2 text-blue-500" />
          <span className="text-gray-900 dark:text-white">{item.name}</span>
        </button>
        {item.children?.map(child => renderFolderOption(child, level + 1))}
      </div>
    );
  };

  const handleSubmit = () => {
    onMove(selectedFolder);
  };


  // Кнопка активна всегда (как для обычных файлов)
  const isSubmitEnabled = () => {
    return true;
  };

  // Рекурсивно ищем элемент по ID
  const findItemById = (items: KnowledgeItem[], id: string): KnowledgeItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children && item.children.length > 0) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div 
            ref={dialogRef}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl border border-gray-200 dark:border-gray-600 ring-1 ring-black/5 dark:ring-white/10"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Переместить в папку
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto mb-4">
              <button
                onClick={() => setSelectedFolder(null)}
                className={`w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center ${
                  selectedFolder === null ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <Folder className="w-4 h-4 mr-2 text-blue-500" />
                <span className="text-gray-900 dark:text-white">Корневая папка</span>
              </button>
              {items.map(item => renderFolderOption(item))}
            </div>


            <div className="flex justify-end space-x-4">
              <button
                onClick={handleSubmit}
                disabled={!isSubmitEnabled()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Переместить
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
